import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Button,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpIcon from '@mui/icons-material/Help';
import HelpDialog from '../HelpDialog/HelpDialog';
import { exportToExcel, exportAnalysisReport, generateAnalysisText, exportCompleteRunToExcel } from '../../utils/exportUtils';
import { useOptimization } from '../../context/OptimizationContext';
import { OptimizationParameters } from '../../types';
import { NotificationSnackbar } from '../common/NotificationSnackbar';
import ParameterPanel from '../ParameterPanel/ParameterPanel';
import DatasetUpload from '../DatasetUpload/DatasetUpload';
import OptimizationControl from '../OptimizationControl/OptimizationControl';
import OptimizationProgress from '../OptimizationProgress/OptimizationProgress';
import ResultsView from '../ResultsView/ResultsView';
import ChartsView from '../ChartsView/ChartsView';
import HistoryView from '../HistoryView/HistoryView';
import Footer from './Footer';
import CompleteRunModal from '../CompleteRunModal/CompleteRunModal';

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const { isRunning, startOptimization, startCompleteRun, cancelOptimization, results, notification, showNotification, hideNotification } = useOptimization();
  const parametersRef = useRef<OptimizationParameters | null>(null);
  const datasetRef = useRef<string>('');
  const [currentParameters, setCurrentParameters] = useState<OptimizationParameters | null>(null);
  const [completeRunModalOpen, setCompleteRunModalOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const handleLogout = () => {
    sessionStorage.removeItem('isAuthenticated');
    sessionStorage.removeItem('username');
    navigate('/');
  };

  const handleParametersChange = (params: {
    taskCount: number;
    qosProperties: number;
    iterations: number;
    initialPopulation: number;
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
    memeplexCount: number;
    numberOfRuns?: number;
  }) => {
    const optParams: OptimizationParameters = {
      taskCount: params.taskCount,
      qosProperties: params.qosProperties,
      iterations: params.iterations,
      initialPopulation: params.initialPopulation,
      selectionRate: params.selectionRate,
      crossoverRate: params.crossoverRate,
      mutationRate: params.mutationRate,
      memeplexCount: params.memeplexCount,
      numberOfRuns: params.numberOfRuns || 1,
    };
    parametersRef.current = optParams;
    setCurrentParameters(optParams);
  };

  const handleLoadPreset = (parameters: OptimizationParameters) => {
    setCurrentParameters(parameters);
    parametersRef.current = parameters;
    showNotification('Preset loaded successfully', 'success');
  };

  const handleFileSelect = (file: File, content: string) => {
    datasetRef.current = content;
  };

  const handleStart = async () => {
    const currentParams = parametersRef.current;
    const currentDataset = datasetRef.current;

    if (!currentParams) {
      showNotification('Please configure parameters first', 'warning');
      return;
    }

    if (!currentDataset) {
      showNotification('Please select a dataset file first', 'warning');
      return;
    }

    await startOptimization(currentParams, currentDataset);
  };

  const handleExportExcel = () => {
    if (!results) {
      showNotification('No results available to export', 'warning');
      return;
    }
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `optimization_results_${timestamp}.xlsx`;
    exportToExcel(results, filename);
    showNotification('Excel file downloaded successfully', 'success');
  };

  const handleExportAnalysis = () => {
    if (!results) {
      showNotification('No results available to export', 'warning');
      return;
    }
    
    const analysisText = generateAnalysisText(results);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `analysis_report_${timestamp}.txt`;
    exportAnalysisReport(analysisText, filename);
    showNotification('Analysis report downloaded successfully', 'success');
  };

  const handleCompleteRun = async (workflowStart: number, workflowEnd: number, increment: number) => {
    const currentParams = parametersRef.current;
    const currentDataset = datasetRef.current;

    if (!currentParams) {
      showNotification('Please configure parameters first', 'warning');
      return;
    }

    if (!currentDataset) {
      showNotification('Please select a dataset file first', 'warning');
      return;
    }

    try {
      const completeRunResults = await startCompleteRun(
        currentParams,
        currentDataset,
        workflowStart,
        workflowEnd,
        increment,
        (current, total, taskCount, params) => {
          // Update UI parameters to show current task count and auto-adjusted parameters
          if (params) {
            setCurrentParameters(params);
            parametersRef.current = params;
          }
          showNotification(
            `Complete Run: ${current}/${total} completed (${taskCount} tasks)`,
            'info'
          );
        }
      );

      // Export results to Excel
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `complete_run_${workflowStart}_to_${workflowEnd}_${timestamp}.xlsx`;
      exportCompleteRunToExcel(completeRunResults, filename);
      showNotification(
        `Complete run finished! ${completeRunResults.length} optimizations completed. Results exported to Excel.`,
        'success'
      );
    } catch (error) {
      console.error('Complete run error:', error);
      showNotification(
        'Complete run failed: ' + (error instanceof Error ? error.message : String(error)),
        'error'
      );
    }
  };

  const hasResults = results !== null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <ScienceIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Service Composition Optimizer
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<HelpIcon />}
              onClick={() => setHelpDialogOpen(true)}
              sx={{ 
                borderColor: 'rgba(255, 255, 255, 0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Help
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{ 
                borderColor: 'rgba(255, 255, 255, 0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Logout
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportExcel}
              disabled={!hasResults}
              sx={{ 
                borderColor: 'rgba(255, 255, 255, 0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-disabled': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              Export Excel
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<AssessmentIcon />}
              onClick={handleExportAnalysis}
              disabled={!hasResults}
              sx={{ 
                borderColor: 'rgba(255, 255, 255, 0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-disabled': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              Export Analysis
            </Button>
          </Box>
          {!isRunning ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<PlayArrowIcon />}
                onClick={handleStart}
                sx={{ minWidth: 180 }}
              >
                Start Optimization
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setCompleteRunModalOpen(true)}
                sx={{ minWidth: 180 }}
              >
                Complete Run
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              color="error"
              size="large"
              startIcon={<StopIcon />}
              onClick={cancelOptimization}
              sx={{ minWidth: 180 }}
            >
              Cancel Optimization
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 3, mb: 3, flexGrow: 1 }}>
        <Grid container spacing={3}>
          {/* Left Sidebar - Parameters and Controls */}
          <Grid item xs={12} md={4} lg={3} sx={{ position: 'sticky', top: 24, alignSelf: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Parameter Configuration */}
              <Paper elevation={2} sx={{ p: 3 }}>
                <ParameterPanel
                  initialValues={currentParameters ? {
                    taskCount: currentParameters.taskCount,
                    qosProperties: currentParameters.qosProperties,
                    iterations: currentParameters.iterations,
                    initialPopulation: currentParameters.initialPopulation,
                    selectionRate: currentParameters.selectionRate,
                    crossoverRate: currentParameters.crossoverRate,
                    mutationRate: currentParameters.mutationRate,
                    memeplexCount: currentParameters.memeplexCount,
                    numberOfRuns: currentParameters.numberOfRuns,
                  } : undefined}
                  onParametersChange={handleParametersChange}
                />
              </Paper>

              {/* Dataset Upload */}
              <Paper elevation={2} sx={{ p: 3 }}>
                <DatasetUpload onFileSelect={handleFileSelect} onError={(msg) => showNotification(msg, 'error')} />
              </Paper>

              {/* Optimization Control */}
              <Paper elevation={2} sx={{ p: 3 }}>
                <OptimizationControl />
              </Paper>
            </Box>
          </Grid>

          {/* Main Content Area - Results and Charts */}
          <Grid item xs={12} md={8} lg={9}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Optimization Progress */}
              <OptimizationProgress />

              {/* Results Tabs */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <ResultsView />
              </Paper>

              {/* Charts */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <ChartsView />
              </Paper>

              {/* History */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <HistoryView />
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Footer />

      {/* Notification Snackbar */}
      {notification && (
        <NotificationSnackbar
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          onClose={hideNotification}
        />
      )}

      {/* Complete Run Modal */}
      <CompleteRunModal
        open={completeRunModalOpen}
        onClose={() => setCompleteRunModalOpen(false)}
        onSubmit={handleCompleteRun}
        defaultStart={currentParameters?.taskCount || 10}
        defaultEnd={1000}
        defaultIncrement={10}
      />

      {/* Help Dialog */}
      <HelpDialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
      />
    </Box>
  );
};

export default MainLayout;

